import React, { useCallback, useEffect } from 'react';
import { StyleSheet, useWindowDimensions } from 'react-native';
import Animated, {
  Easing,
  useAnimatedStyle,
  useSharedValue,
  withRepeat,
  withTiming,
} from 'react-native-reanimated';
import { useStyle } from 'react-native-style-utilities';

import { pointerEventsStyles, Image } from '@sg/garnish';

import { HexagonGlass } from '../../assets';

// ─────────────────────────────────────────────────────────────────────────────

/**
 * A large translucent hexagon that drifts behind the permissions primer content.
 * Purely decorative — it anchors off the right edge and rotates slowly enough to
 * read as ambient motion rather than an animation.
 */
export const PermissionsPromptHexagonGlass = () => {
  const { width: viewportWidth } = useWindowDimensions();

  // ─── Shared Values ───────────────────────────────────────────────────

  const rotateSV = useSharedValue(HEXAGON_GLASS_INITIAL_ROTATE_DEG);
  const opacitySV = useSharedValue(0);

  // ─── Handlers ────────────────────────────────────────────────────────

  const startRotation = useCallback(() => {
    rotateSV.value = withRepeat(
      withTiming(HEXAGON_GLASS_INITIAL_ROTATE_DEG + 360, {
        duration: HEXAGON_GLASS_ROTATE_DURATION,
        easing: Easing.linear,
      }),
      -1,
      false,
    );
  }, [rotateSV]);

  const stopRotation = useCallback(() => {
    rotateSV.value = HEXAGON_GLASS_INITIAL_ROTATE_DEG;
  }, [rotateSV]);

  const startFadeIn = useCallback(() => {
    opacitySV.value = withTiming(1, { duration: HEXAGON_GLASS_ENTERING_DURATION });
  }, [opacitySV]);

  const triggerAnimations = useCallback(() => {
    startRotation();
    startFadeIn();

    return stopRotation;
  }, [startRotation, startFadeIn, stopRotation]);

  // ─── Effects ─────────────────────────────────────────────────────────

  useEffect(triggerAnimations, [triggerAnimations]);

  // ─── Styles ──────────────────────────────────────────────────────────

  const containerDynamicStyles = useStyle(
    () => ({ width: viewportWidth * HEXAGON_GLASS_WIDTH_RATIO }),
    [viewportWidth],
  );

  const containerAnimatedStyles = useAnimatedStyle(() => ({
    opacity: opacitySV.value,
    transform: [
      { translateX: HEXAGON_GLASS_TRANSLATE_X },
      { translateY: HEXAGON_GLASS_TRANSLATE_Y },
      { rotate: `${rotateSV.value}deg` },
    ],
  }));

  const containerStyles = [styles.container, containerDynamicStyles, containerAnimatedStyles];

  // ─────────────────────────────────────────────────────────────────────

  return (
    <Animated.View style={[containerStyles, pointerEventsStyles.none]} accessible={false}>
      <Image source={HexagonGlass} style={styles.image} contentFit="contain" accessible={false} />
    </Animated.View>
  );
};

// ─── Constants ───────────────────────────────────────────────────────────────

const HEXAGON_GLASS_WIDTH_RATIO = 1.7;
const HEXAGON_GLASS_TRANSLATE_X = '50%';
const HEXAGON_GLASS_TRANSLATE_Y = '-10%';
const HEXAGON_GLASS_TOP = 60;
const HEXAGON_GLASS_INITIAL_ROTATE_DEG = 90;
const HEXAGON_GLASS_ROTATE_DURATION = 120_000;
const HEXAGON_GLASS_ENTERING_DURATION = 1400;

// ─── Styles ──────────────────────────────────────────────────────────────────

const styles = StyleSheet.create({
  container: {
    position: 'absolute',
    top: HEXAGON_GLASS_TOP,
    right: 0,
    aspectRatio: 1,
  },
  image: {
    width: '100%',
    height: '100%',
  },
});
